import React, { useState, useRef, useEffect } from "react";
import styles from "./LanguagesSelect.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../redux/store";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { VALID_LANGS, LangCode, findRouteKey, buildLangPath } from "../../../constants/route-slugs";
import { setLanguage } from "../../../redux/Slices/languageSlice";

const PRIVATE_PREFIXES = ["/account", "/auth", "/payment-success", "/payment-failed", "/server-error"];

const LanguageSelect: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const language = useSelector((state: RootState) => state.language.language);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang: urlLang } = useParams<{ lang: string }>();

  const handleLanguageChange = (newLang: string) => {
    setIsOpen(false);

    // Private routes — only update language, don't change URL
    const isPrivate = PRIVATE_PREFIXES.some((p) => location.pathname.startsWith(p));
    if (isPrivate) {
      dispatch(setLanguage(newLang));
      localStorage.setItem("language", newLang);
      document.documentElement.lang = newLang;
      return;
    }

    if (newLang === urlLang) return;

    const segments = location.pathname.split("/").filter(Boolean);
    const currentSlug = segments[1];

    if (!currentSlug) {
      navigate(`/${newLang}`);
      return;
    }

    const routeKey = findRouteKey(currentSlug);
    if (routeKey) {
      navigate(buildLangPath(newLang as LangCode, routeKey));
    } else {
      const rest = segments.slice(1).join("/");
      navigate(`/${newLang}/${rest}`);
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentLang = (
    urlLang && VALID_LANGS.includes(urlLang as LangCode) ? urlLang : language
  ).toUpperCase();

  return (
    <div className={styles.langSelect} ref={dropdownRef}>
      <div className={styles.langSelect__selected} onClick={() => setIsOpen(!isOpen)}>
        {currentLang}
        <span className={styles.arrow}>&#9662;</span>
      </div>
      {isOpen && (
        <ul className={styles.langSelect__options}>
          <li onClick={() => handleLanguageChange("az")}>AZ</li>
          <li onClick={() => handleLanguageChange("en")}>EN</li>
          <li onClick={() => handleLanguageChange("ru")}>RU</li>
        </ul>
      )}
    </div>
  );
};

export default LanguageSelect;
